import React from 'react';
import {
  Clock,
  FileText,
  Minus,
  Phone,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import {
  Order,
  OrderSheet,
  Product,
  ProductOrderHistoryDetail,
  SellerWaitlistEntry,
} from '../../types';
import { formatPhoneNumber } from '../../utils/formatters';
import { updateOrder } from '../../api/orders';
import {
  getSellerProductHistoryDetail,
  getSellerProductCancelledHistory,
  ProductCancelledHistoryRow,
} from '../../api/seller-page-details';
import { getSellerProductWaitlist } from '../../api/waitlists';

const ORDER_TIME_DIFFERENCE_THRESHOLD_MS = 60 * 1000;

const hasMeaningfulTimeDifference = (a?: string | null, b?: string | null) => {
  const first = a ? new Date(a).getTime() : NaN;
  const second = b ? new Date(b).getTime() : NaN;

  if (!Number.isFinite(first) || !Number.isFinite(second)) {
    return false;
  }

  return first - second > ORDER_TIME_DIFFERENCE_THRESHOLD_MS;
};

interface ProductOrderHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  option?: {
    id: string;
    name: string;
  };
  selectedOrderSheetIds: string[];
  orderSheets?: OrderSheet[];
  onRefresh?: () => Promise<void> | void;
  onViewCustomerHistory?: (
    name: string,
    phone: string,
    userId?: string,
    orderSheetId?: string,
  ) => void;
  cancelled?: boolean;
}

export const ProductOrderHistoryModal: React.FC<ProductOrderHistoryModalProps> = ({
  isOpen,
  onClose,
  product,
  option,
  selectedOrderSheetIds,
  orderSheets = [],
  onRefresh,
  onViewCustomerHistory,
  cancelled = false,
}) => {
  const [activeTab, setActiveTab] = React.useState<'orders' | 'waitlist'>(
    'orders',
  );
  const [orders, setOrders] = React.useState<ProductOrderHistoryDetail[]>([]);
  const [waitlistEntries, setWaitlistEntries] = React.useState<
    SellerWaitlistEntry[]
  >([]);
  const [cancelledRows, setCancelledRows] = React.useState<
    ProductCancelledHistoryRow[]
  >([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [isUpdating, setIsUpdating] = React.useState<string | null>(null);

  const resolvedOrderSheetIds = React.useMemo(() => {
    if (selectedOrderSheetIds.includes('all')) {
      return orderSheets.map((sheet) => sheet.id);
    }
    return selectedOrderSheetIds;
  }, [orderSheets, selectedOrderSheetIds]);

  const loadHistory = React.useCallback(async () => {
    if (!isOpen) {
      return;
    }

    setIsLoading(true);
    setLoadError(null);

    if (cancelled) {
      try {
        const rows = await getSellerProductCancelledHistory({
          productId: product.id,
          productOptionId: option?.id,
          orderSheetIds: resolvedOrderSheetIds,
        });
        setCancelledRows(rows);
      } catch (error) {
        console.error('Failed to load cancelled history:', error);
        setLoadError('취소 내역을 불러오지 못했습니다.');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    try {
      const [nextOrders, nextWaitlistEntries] = await Promise.all([
        getSellerProductHistoryDetail({
          productId: product.id,
          productOptionId: option?.id,
          orderSheetIds: resolvedOrderSheetIds,
        }),
        getSellerProductWaitlist({
          productId: product.id,
          productOptionId: option?.id,
          orderSheetIds: resolvedOrderSheetIds,
        }),
      ]);
      setOrders(nextOrders);
      setWaitlistEntries(nextWaitlistEntries);
    } catch (error) {
      console.error('Failed to load product customer lists:', error);
      setLoadError('고객 명단을 불러오지 못했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, option?.id, product.id, resolvedOrderSheetIds, cancelled]);

  React.useEffect(() => {
    if (!isOpen) {
      setActiveTab('orders');
      setOrders([]);
      setWaitlistEntries([]);
      setCancelledRows([]);
      setLoadError(null);
      setIsUpdating(null);
      return;
    }

    void loadHistory();
  }, [isOpen, loadHistory]);

  React.useEffect(() => {
    setActiveTab('orders');
  }, [cancelled, option?.id, product.id]);

  const isTargetItem = React.useCallback(
    (item: Order['items'][number]) =>
      item.productId === product.id &&
      (!option || item.productOptionId === option.id),
    [option, product.id],
  );

  const handleUpdateQuantity = async (orderId: string, newQuantity: number) => {
    const order = orders.find((candidate) => candidate.id === orderId);
    if (!order) return;

    if (newQuantity <= 0) {
      await handleCancelProduct(orderId);
      return;
    }

    try {
      setIsUpdating(orderId);
      const updatedItems = order.items.map((item) =>
        isTargetItem(item) ? { ...item, quantity: newQuantity } : item,
      );

      const totalPrice = updatedItems.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0,
      );

      await updateOrder(orderId, {
        items: updatedItems,
        totalPrice,
        isForced: true,
      });

      await onRefresh?.();
      await loadHistory();
    } catch (error) {
      console.error('Failed to update quantity', error);
      alert('수량 변경에 실패했습니다.');
    } finally {
      setIsUpdating(null);
    }
  };

  const handleCancelProduct = async (orderId: string) => {
    const order = orders.find((candidate) => candidate.id === orderId);
    if (!order) return;

    const hasOtherItems = order.items.some((item) => !isTargetItem(item));
    const targetLabel = option ? `${product.name} - ${option.name}` : product.name;
    const confirmMsg = hasOtherItems
      ? `이 주문에서 [${targetLabel}] 상품을 제외하시겠습니까?`
      : `이 주문을 전체 취소하시겠습니까? (상품이 1개뿐입니다)`;

    if (!window.confirm(confirmMsg)) return;

    try {
      setIsUpdating(orderId);
      if (!hasOtherItems) {
        await updateOrder(orderId, {
          status: 'CANCELLED',
        });
      } else {
        const updatedItems = order.items.filter((item) => !isTargetItem(item));
        const totalPrice = updatedItems.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0,
        );

        await updateOrder(orderId, {
          items: updatedItems,
          totalPrice,
          isForced: true,
        });
      }

      await onRefresh?.();
      await loadHistory();
    } catch (error) {
      console.error('Failed to cancel order/product', error);
      alert('취소 처리에 실패했습니다.');
    } finally {
      setIsUpdating(null);
    }
  };

  const historyItems = React.useMemo(() => {
    if (cancelled) {
      return cancelledRows.map((row) => ({
        orderId: row.orderId,
        customerName: row.customerName,
        customerPhone: row.customerPhone || '',
        quantity: row.cancelledQty,
        date: row.cancelledAt,
        originalOrderDate: row.cancelledAt,
        isAdditionalOrder: false,
        isEstimated: false,
        status: 'CANCELLED' as Order['status'],
        request: row.request || undefined,
      }));
    }

    return orders
      .flatMap((order) => {
        const itemsForProduct = order.items.filter(isTargetItem);
        const quantity = itemsForProduct.reduce(
          (sum, item) => sum + (item.quantity || 0),
          0,
        );

        if (quantity <= 0) {
          return [];
        }

        const orderDate = order.productOrderedAt;
        const originalOrderDate = order.createdAt || order.date;

        return [{
          orderId: order.id,
          customerName: order.customerName,
          customerPhone: order.customerPhone || '',
          quantity,
          date: orderDate,
          originalOrderDate,
          isAdditionalOrder: hasMeaningfulTimeDifference(
            orderDate,
            originalOrderDate,
          ),
          isEstimated: order.isProductOrderedAtEstimated,
          status: order.status,
          request: order.request,
          userId: order.userId,
          orderSheetId: order.orderSheetId,
        }];
      })
      .sort(
        (a, b) =>
          new Date(a.date).getTime() - new Date(b.date).getTime(),
      );
  }, [cancelled, cancelledRows, isTargetItem, orders]);

  if (!isOpen) return null;

  const totalOrdered = historyItems.reduce((sum, item) => sum + item.quantity, 0);
  const isWaitlistTab = !cancelled && activeTab === 'waitlist';
  const totalWaiting = waitlistEntries.reduce(
    (sum, entry) => sum + entry.remainingQuantity,
    0,
  );
  const label = cancelled
    ? '취소 주문 고객 명단'
    : isWaitlistTab
      ? '대기 수량 명단'
      : '주문 고객 명단';
  const title = option
    ? `[${product.name} - ${option.name}] ${label}`
    : `[${product.name}] ${label}`;

  return (
    <div className="fixed inset-0 z-[7000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl bg-white rounded-[32px] flex flex-col max-h-[90vh] shadow-2xl animate-in zoom-in duration-200">
        <div className="p-6 border-b flex justify-between items-center shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-xl font-black text-gray-900">{title}</h4>
            </div>
            <p
              className={`text-sm font-bold mt-1 ${
                cancelled
                  ? 'text-red-500'
                  : isWaitlistTab
                    ? 'text-violet-600'
                    : 'text-emerald-600'
              }`}
            >
              총 {isWaitlistTab ? totalWaiting : totalOrdered}개{' '}
              {cancelled ? '취소됨' : isWaitlistTab ? '대기 중' : '주문됨'}
              {!isWaitlistTab && ` (${historyItems.length}명)`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-gray-100 rounded-full text-gray-400 hover:bg-gray-200 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {!cancelled && (
          <div className="px-6 pt-4 shrink-0">
            <div
              className="grid grid-cols-2 gap-1 rounded-2xl bg-gray-100 p-1"
              role="tablist"
              aria-label="상품 고객 명단"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'orders'}
                onClick={() => setActiveTab('orders')}
                className={`rounded-xl px-4 py-2.5 text-sm font-black transition-all ${
                  activeTab === 'orders'
                    ? 'bg-white text-emerald-600 shadow-sm'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                주문 고객 {historyItems.length}명
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'waitlist'}
                onClick={() => setActiveTab('waitlist')}
                className={`rounded-xl px-4 py-2.5 text-sm font-black transition-all ${
                  activeTab === 'waitlist'
                    ? 'bg-white text-violet-600 shadow-sm'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                대기 수량 {totalWaiting}개
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6 space-y-4 no-scrollbar">
          {isLoading ? (
            <div className="py-20 flex flex-col items-center justify-center text-gray-400 gap-2">
              <FileText size={48} strokeWidth={1} />
              <p className="text-sm font-bold">고객 명단을 불러오는 중입니다.</p>
            </div>
          ) : loadError ? (
            <div className="py-20 flex flex-col items-center justify-center text-gray-400 gap-2">
              <FileText size={48} strokeWidth={1} />
              <p className="text-sm font-bold">{loadError}</p>
            </div>
          ) : (isWaitlistTab
              ? waitlistEntries.length === 0
              : historyItems.length === 0) ? (
            <div className="py-20 flex flex-col items-center justify-center text-gray-400 gap-2">
              <FileText size={48} strokeWidth={1} />
              <p className="text-sm font-bold">
                {cancelled
                  ? '취소 내역이 없습니다.'
                  : isWaitlistTab
                    ? '현재 대기 수량이 없습니다.'
                    : '주문 내역이 없습니다.'}
              </p>
            </div>
          ) : isWaitlistTab ? (
            <div className="space-y-3">
              {waitlistEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="group rounded-2xl border border-violet-100 bg-violet-50/30 p-4 transition-all hover:bg-white hover:shadow-sm"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-violet-100 bg-white text-violet-600 transition-colors">
                        <span className="text-xs font-black">
                          {entry.remainingQuantity}개
                        </span>
                      </div>
                      {onViewCustomerHistory ? (
                        <button
                          type="button"
                          onClick={() =>
                            onViewCustomerHistory(
                              entry.customerName,
                              entry.customerPhone,
                              entry.userId,
                              entry.orderSheetId,
                            )
                          }
                          className="min-w-0 rounded-xl text-left focus:outline-none focus:ring-2 focus:ring-violet-500/30 group/customer"
                          title="고객 주문 이력 보기"
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-black text-gray-900 transition-colors group-hover/customer:text-violet-600">
                              {entry.customerName}
                            </span>
                            {entry.optionName && !option && (
                              <span className="rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-black text-violet-600">
                                {entry.optionName}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                            <div className="flex items-center gap-1 text-[11px] font-bold text-gray-400 transition-colors group-hover/customer:text-gray-500">
                              <Phone size={12} />
                              {formatPhoneNumber(entry.customerPhone)}
                            </div>
                            <div className="flex items-center gap-1 text-[11px] font-bold text-gray-400 transition-colors group-hover/customer:text-gray-500">
                              <Clock size={12} />
                              줄서기{' '}
                              {new Date(entry.createdAt).toLocaleString('ko-KR', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </div>
                        </button>
                      ) : (
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-black text-gray-900">
                              {entry.customerName}
                            </span>
                            {entry.optionName && !option && (
                              <span className="rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-black text-violet-600">
                                {entry.optionName}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                            <div className="flex items-center gap-1 text-[11px] font-bold text-gray-400">
                              <Phone size={12} />
                              {formatPhoneNumber(entry.customerPhone)}
                            </div>
                            <div className="flex items-center gap-1 text-[11px] font-bold text-gray-400">
                              <Clock size={12} />
                              줄서기{' '}
                              {new Date(entry.createdAt).toLocaleString('ko-KR', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 rounded-xl border border-violet-100 bg-white px-3 py-2 text-center">
                      <p className="text-[10px] font-bold text-gray-400">
                        남은 수량
                      </p>
                      <p className="text-sm font-black text-violet-600">
                        {entry.remainingQuantity}개
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {historyItems.map((item, index) => (
                <div
                  key={item.orderId}
                  className="bg-gray-50/50 rounded-2xl p-4 border border-gray-100 hover:bg-white hover:shadow-sm transition-all group"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-white rounded-xl border flex items-center justify-center text-gray-400 group-hover:text-emerald-500 transition-colors">
                        <span className="text-xs font-black">{index + 1}</span>
                      </div>
                      {onViewCustomerHistory ? (
                        <button
                          type="button"
                          onClick={() =>
                            onViewCustomerHistory(
                              item.customerName,
                              item.customerPhone,
                              item.userId,
                              item.orderSheetId,
                            )
                          }
                          className="text-left rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/30 group/customer"
                          title="고객 주문 이력 보기"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-black text-gray-900 group-hover/customer:text-emerald-600 transition-colors">
                              {item.customerName}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                            <div className="flex items-center gap-1 text-[11px] font-bold text-gray-400 group-hover/customer:text-gray-500 transition-colors">
                              <Phone size={12} />
                              {formatPhoneNumber(item.customerPhone)}
                            </div>
                            <div className="flex items-center gap-1 text-[11px] font-bold text-gray-400 group-hover/customer:text-gray-500 transition-colors">
                              <Clock size={12} />
                              {cancelled ? '취소' : '상품 주문'}{' '}
                              {new Date(item.date).toLocaleString('ko-KR', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                            {item.isAdditionalOrder && (
                              <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-md">
                                추가 주문
                              </span>
                            )}
                            {item.isEstimated && (
                              <span className="text-[10px] font-black text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-md">
                                추정 시각
                              </span>
                            )}
                          </div>
                        </button>
                      ) : (
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-black text-gray-900">
                              {item.customerName}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                            <div className="flex items-center gap-1 text-[11px] font-bold text-gray-400">
                              <Phone size={12} />
                              {formatPhoneNumber(item.customerPhone)}
                            </div>
                            <div className="flex items-center gap-1 text-[11px] font-bold text-gray-400">
                              <Clock size={12} />
                              {cancelled ? '취소' : '상품 주문'}{' '}
                              {new Date(item.date).toLocaleString('ko-KR', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                            {item.isAdditionalOrder && (
                              <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-md">
                                추가 주문
                              </span>
                            )}
                            {item.isEstimated && (
                              <span className="text-[10px] font-black text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-md">
                                추정 시각
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-1 font-mono">
                          Quantity
                        </div>
                        <div className="flex items-center gap-2 bg-white rounded-xl p-1 border border-gray-100 shadow-sm">
                          {!cancelled && (
                            <button
                              onClick={() =>
                                handleUpdateQuantity(item.orderId, item.quantity - 1)
                              }
                              disabled={isUpdating === item.orderId}
                              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-50 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-30"
                            >
                              <Minus size={14} />
                            </button>
                          )}
                          <span className="min-w-[20px] text-center text-sm font-black text-gray-900">
                            {item.quantity}
                          </span>
                          {!cancelled && (
                            <button
                              onClick={() =>
                                handleUpdateQuantity(item.orderId, item.quantity + 1)
                              }
                              disabled={isUpdating === item.orderId}
                              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-50 text-gray-400 hover:text-emerald-500 transition-colors disabled:opacity-30"
                            >
                              <Plus size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                      {!cancelled && (
                        <button
                          onClick={() => handleCancelProduct(item.orderId)}
                          disabled={isUpdating === item.orderId}
                          className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all disabled:opacity-30"
                          title="취소/제외"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                  {item.request && (
                    <div className="mt-3 bg-amber-50 rounded-xl p-2.5 border border-amber-100/50">
                      <p className="text-[10px] font-black text-amber-600 mb-0.5 uppercase tracking-tighter">
                        요청사항
                      </p>
                      <p className="text-[11px] font-bold text-gray-600 leading-relaxed">
                        {item.request}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-gray-50/50 rounded-b-[32px]">
          <p className="text-[11px] text-gray-400 font-bold text-center">
            {cancelled ? (
              '* 해당 상품 주문이 취소된 고객 명단입니다.'
            ) : isWaitlistTab ? (
              <>
                * 순번은 같은 상품/옵션의 전체 열린 주문지 대기열 기준입니다. <br />
                재고가 확보되면 선착순으로 주문에 자동 반영됩니다.
              </>
            ) : (
              <>
                * 해당 상품이 실제로 주문/추가된 시간 순서(먼저 주문한 순)로 표시됩니다. <br />
                '추정 시각'은 상품 변경 이력이 없는 과거 주문입니다. <br />
                재고 부족 시 나중에 주문한 고객부터 취소를 검토해 주세요.
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};
